import { computed } from 'vue'
import { WORK_ORDERS_OVERVIEW_TRANSLATION_NAMESPACE as NS } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/config/constants'

const COLUMNS_COUNT = 2

/**
 * The "Podaci o zadatku" fields of a work order, grouped into the blocks the design
 * shows.
 *
 * Every one of them is read-only: an order is what the plan froze at the moment it was
 * raised, and the backend exposes it through `index` and `show` and nothing else - so
 * there is no field here that could be saved even if it were unlocked.
 *
 * The values are the flat ones `workOrderData` derives from the nested record.
 */
export function useWorkOrderTaskFields (options) {
  const { translate } = options || {}

  // `creatable` stays false alongside `editable`: it is what Content.vue reads to
  // decide what goes into a payload, and this screen sends none.
  const readOnlyField = (key, label) => ({
    key,
    label,
    editable: false,
    creatable: false,
    visible: true
  })

  const basicDataRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.basic_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      readOnlyField('status', translate(`${NS}.status`)),
      readOnlyField('id', translate(`${NS}.id`)),
      readOnlyField('task_name', translate(`${NS}.task_name`)),
      readOnlyField('task_type', translate(`${NS}.task_type`)),
      readOnlyField('surface_type', translate(`${NS}.surface_type`)),
      readOnlyField('task_category', translate(`${NS}.task_category`))
    ]
  }))

  const cleaningRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.cleaning_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      readOnlyField('cleaning_type', translate(`${NS}.cleaning_type`))
    ]
  }))

  const vehicleRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.vehicle_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      readOnlyField('vehicle_type', translate(`${NS}.vehicle_type`)),
      readOnlyField('drive_type', translate(`${NS}.drive_type`)),
      readOnlyField('registration', translate(`${NS}.registration`))
    ]
  }))

  const crewRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.crew_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      // The driver the plan was made with; who actually drove the order is on the
      // listing beside it.
      readOnlyField('driver', translate(`${NS}.driver`)),
      readOnlyField('worker_count', translate(`${NS}.worker_count`))
    ]
  }))

  const spreadingRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.spreading_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      readOnlyField('spread_type', translate(`${NS}.spread_type`)),
      readOnlyField('spread_quantity', translate(`${NS}.spread_quantity`)),
      readOnlyField('throw_side', translate(`${NS}.throw_side`))
    ]
  }))

  const contractorRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.contractor_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      readOnlyField('contractor', translate(`${NS}.contractor`)),
      readOnlyField('responsible_base', translate(`${NS}.responsible_base`)),
      readOnlyField('operator', translate(`${NS}.operator`)),
      readOnlyField('base_responsible_person', translate(`${NS}.base_responsible_person`))
    ]
  }))

  const rows = computed(() => ([
    basicDataRow.value,
    cleaningRow.value,
    vehicleRow.value,
    crewRow.value,
    spreadingRow.value,
    contractorRow.value
  ]))

  return {
    rows
  }
}
